import { useCallback, useState } from 'react';
/**
 * Utility hook for easier state management of multiple checkboxes.
 * This is especially useful for "Select All" checkboxes whose state
 * can be indeterminate (-).
 *
 * @param checkboxIds Array of all checkbox ids
 * @param preSelectedIds Array of pre-selected checkbox ids (if any)
 * @param preDisabledIds Array of pre-disabled checkbox ids (if any)
 *
 * @return
 * - checkedItems,
 * - disabledItems,
 * - handleChange,
 * - toggleAll,
 * - selectAll,
 * - clearAll,
 * - enableCheckboxes,
 * - disableCheckboxes,
 */
export const useCheckboxState = (checkboxIds, preSelectedIds = [], preDisabledIds = []) => {
    const numTotalItems = checkboxIds.length;
    const [checkedItems, setCheckedItems] = useState(new Set(preSelectedIds));
    const [disabledItems, setDisabledItems] = useState(new Set(preDisabledIds));
    /**
     * Pass this function into "onChange" prop of all checkboxes
     * EXCEPT the "select all" checkbox. (See toggleAll function)
     */
    const handleChange = useCallback((event, id) => {
        // Capturing target because setCheckedItems is async
        // and event.target sometimes becomes null
        const { target } = event;
        setCheckedItems((prevState) => {
            const newState = new Set(prevState);
            if (target.checked) {
                newState.add(id);
            }
            else {
                newState.delete(id);
            }
            return newState;
        });
    }, []);
    /**
     * Helper function to change the state of all checkboxes.
     * This function will NOT change the state of disabled items.
     */
    const setAll = useCallback((isChecked) => {
        setCheckedItems((prevState) => {
            const newState = new Set(prevState);
            checkboxIds.forEach((id) => {
                // Skip disabled items
                if (!disabledItems.has(id)) {
                    if (isChecked) {
                        newState.add(id);
                    }
                    else {
                        newState.delete(id);
                    }
                }
            });
            return newState;
        });
    }, [checkboxIds, disabledItems]);
    /**
     * Check all checkboxes
     */
    const selectAll = useCallback(() => {
        if (checkedItems.size === numTotalItems) {
            return;
        }
        setAll(true);
    }, [checkedItems, numTotalItems, setAll]);
    /**
     * Uncheck all checkboxes
     */
    const clearAll = useCallback(() => {
        if (checkedItems.size === 0) {
            return;
        }
        setAll(false);
    }, [checkedItems, setAll]);
    /**
     * Toggles the state of all checkboxes
     * Use this for "onChange" prop of "Select All" checkbox.
     *
     * Behavior:
     * 0 items checked => select all
     * 1 or more items checked => clear all
     */
    const toggleAll = useCallback(() => {
        // Number of checkboxes that are both disabled and checked.
        // This is required for the correct behavior of toggleAll
        // when there are disabled checkboxes.
        const numDisabledAndChecked = [...disabledItems].reduce((count, id) => {
            if (checkedItems.has(id)) {
                return count + 1;
            }
            return count;
        }, 0);
        // If 0 normal checkboxes are checked, select all.
        if (checkedItems.size - numDisabledAndChecked === 0) {
            selectAll();
        }
        else {
            clearAll();
        }
    }, [checkedItems, disabledItems, selectAll, clearAll]);
    /**
     * Enable checkboxes. Accepts one or more ids.
     */
    const enableCheckboxes = useCallback((...ids) => {
        setDisabledItems((prevState) => {
            const newState = new Set(prevState);
            ids.forEach((id) => newState.delete(id));
            return newState;
        });
    }, []);
    /**
     * Disable checkboxes. Accepts one or more ids.
     */
    const disableCheckboxes = useCallback((...ids) => {
        setDisabledItems((prevState) => {
            const newState = new Set(prevState);
            ids.forEach((id) => newState.add(id));
            return newState;
        });
    }, []);
    return {
        checkedItems,
        setCheckedItems,
        disabledItems,
        handleChange,
        toggleAll,
        selectAll,
        clearAll,
        enableCheckboxes,
        disableCheckboxes,
    };
};
//# sourceMappingURL=checkbox-utils.js.map